import { CellPosition, SpreadsheetData } from './types';

// Helper function to convert number to Excel column letter (0 -> A, 1 -> B, etc.)
export const numberToColumnLetter = (num: number): string => {
  let result = '';
  while (num >= 0) {
    result = String.fromCharCode(65 + (num % 26)) + result;
    num = Math.floor(num / 26) - 1;
  }
  return result;
};

// Helper function to get column index from key
export const getColumnIndex = (columnKey: string): number => {
  if (columnKey === '__rowNumber') return -1;
  
  let result = 0;
  for (let i = 0; i < columnKey.length; i++) {
    result = result * 26 + (columnKey.charCodeAt(i) - 65 + 1);
  }
  return result - 1;
};

// Helper function to create cell key
export const getCellKey = (rowIdx: number, columnKey: string): string => {
  return `${rowIdx}-${columnKey}`;
};

// Helper function to convert cell key back to position
export const parseCellKey = (cellKey: string): CellPosition => {
  const [rowIdx, columnKey] = cellKey.split('-');
  return { rowIdx: parseInt(rowIdx), columnKey };
};

// Helper function to select range of cells
export const selectRange = (start: CellPosition, end: CellPosition): Set<string> => {
  const newSelectedCells = new Set<string>();
  const startColIdx = getColumnIndex(start.columnKey);
  const endColIdx = getColumnIndex(end.columnKey);
  
  if (startColIdx === -1 || endColIdx === -1) return newSelectedCells;

  const minRow = Math.min(start.rowIdx, end.rowIdx);
  const maxRow = Math.max(start.rowIdx, end.rowIdx);
  const minCol = Math.min(startColIdx, endColIdx);
  const maxCol = Math.max(startColIdx, endColIdx);

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      const columnKey = numberToColumnLetter(col);
      newSelectedCells.add(getCellKey(row, columnKey));
    }
  }

  return newSelectedCells;
};

// Helper function to check if data has changed
export const hasDataChanged = (currentData: SpreadsheetData[], originalData: SpreadsheetData[]): boolean => {
  if (originalData.length !== currentData.length) {
    return true;
  }
  
  for (let i = 0; i < currentData.length; i++) {
    const currentRow = currentData[i];
    const originalRow = originalData[i];
    
    if (!originalRow) return true;
    
    // Check if any cell in this row has changed
    const currentKeys = Object.keys(currentRow);
    const originalKeys = Object.keys(originalRow);
    
    if (currentKeys.length !== originalKeys.length) {
      return true;
    }
    
    for (const key of currentKeys) {
      if (currentRow[key] !== originalRow[key]) {
        return true;
      }
    }
  }
  
  return false;
};

// Helper function to create empty spreadsheet data
export const createEmptySpreadsheetData = (rows: number, columns: number): SpreadsheetData[] => {
  const EMPTY_CELL = '';
  
  return Array.from({ length: rows }, () => {
    const row: SpreadsheetData = {};
    for (let i = 0; i < columns; i++) {
      const columnKey = numberToColumnLetter(i);
      row[columnKey] = EMPTY_CELL;
    }
    return row;
  });
};

// Helper function to ensure data has correct dimensions
export const ensureDataDimensions = (
  data: SpreadsheetData[], 
  rows: number, 
  columns: number
): SpreadsheetData[] => {
  const EMPTY_CELL = '';
  const result = [...data];
  
  // Ensure all existing rows have all columns
  result.forEach(row => {
    for (let i = 0; i < columns; i++) {
      const columnKey = numberToColumnLetter(i);
      if (!(columnKey in row)) {
        row[columnKey] = EMPTY_CELL;
      }
    }
  });
  
  // Add empty rows if needed
  while (result.length < rows) {
    const emptyRow: SpreadsheetData = {};
    for (let i = 0; i < columns; i++) {
      const columnKey = numberToColumnLetter(i);
      emptyRow[columnKey] = EMPTY_CELL;
    }
    result.push(emptyRow);
  }
  
  return result;
};

// Helper function to convert a number to its ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
export const getOrdinalSuffix = (num: number): string => {
  const lastDigit = num % 10;
  const lastTwoDigits = num % 100;
  
  // Special cases for 11th, 12th, 13th
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${num}th`;
  }
  
  switch (lastDigit) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
};

// Helper function to convert selected cells to clipboard data
export const convertSelectedCellsToClipboard = (
  selectedCells: Set<string>,
  spreadsheetData: SpreadsheetData[]
): string[][] => {
  const EMPTY_CELL = '';
  
  // Convert selected cells to positions and sort them
  const positions: CellPosition[] = Array.from(selectedCells).map(cellKey => parseCellKey(cellKey));

  // Sort by row first, then by column
  positions.sort((a, b) => {
    if (a.rowIdx !== b.rowIdx) return a.rowIdx - b.rowIdx;
    return getColumnIndex(a.columnKey) - getColumnIndex(b.columnKey);
  });

  // Find the bounding rectangle of selected cells
  const rows = positions.map(p => p.rowIdx);
  const cols = positions.map(p => getColumnIndex(p.columnKey));
  const minRow = Math.min(...rows);
  const maxRow = Math.max(...rows);
  const minCol = Math.min(...cols);
  const maxCol = Math.max(...cols);

  // Create 2D array for the copied data
  const copiedData: string[][] = [];
  
  for (let row = minRow; row <= maxRow; row++) {
    const rowData: string[] = [];
    for (let col = minCol; col <= maxCol; col++) {
      const columnKey = numberToColumnLetter(col);
      const cellKey = getCellKey(row, columnKey);
      
      if (selectedCells.has(cellKey)) {
        // Get the value from spreadsheet data
        const cellValue = spreadsheetData[row]?.[columnKey] || EMPTY_CELL;
        rowData.push(cellValue);
      } else {
        // Empty cell in the selection rectangle
        rowData.push('');
      }
    }
    copiedData.push(rowData);
  }

  return copiedData;
}; 